'use client';

import {
  CircleCheck,
  Info,
  LoaderCircle,
  OctagonX,
  TriangleAlert,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import { Toaster as Sonner } from 'sonner';

import { useIsMobile } from '../hooks/use-mobile';
import { useUiLabels } from './ui-labels';

type ToasterProps = React.ComponentProps<typeof Sonner>;

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = 'system' } = useTheme();
  const isMobile = useIsMobile();
  const labels = useUiLabels();

  return (
    <Sonner
      theme={theme as ToasterProps['theme']}
      containerAriaLabel={labels.notifications}
      className="toaster group z-[10001]"
      position={isMobile ? 'bottom-center' : 'bottom-right'}
      toastOptions={{
        classNames: {
          toast:
            'group toast group-[.toaster]:bg-card group-[.toaster]:text-card-foreground group-[.toaster]:border group-[.toaster]:border-border group-[.toaster]:rounded-lg group-[.toaster]:shadow-sm group-[.toaster]:px-4 group-[.toaster]:py-3',
          description:
            'group-[.toast]:text-muted-foreground group-[.toast]:text-sm',
          title: 'group-[.toast]:text-sm group-[.toast]:font-medium',
          actionButton:
            'group-[.toast]:bg-primary-solid group-[.toast]:text-primary-solid-foreground group-[.toast]:rounded-md group-[.toast]:px-3 group-[.toast]:py-1.5 group-[.toast]:text-xs group-[.toast]:font-medium group-[.toast]:hover:bg-primary-solid-hover',
          cancelButton:
            'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground group-[.toast]:rounded-md group-[.toast]:px-3 group-[.toast]:py-1.5 group-[.toast]:text-xs group-[.toast]:font-medium group-[.toast]:hover:bg-muted/80',
          closeButton:
            'group-[.toast]:text-muted-foreground group-[.toast]:hover:text-foreground group-[.toast]:hover:bg-muted',
        },
      }}
      style={
        isMobile
          ? {
              // Clear Cap tab bar (6rem base) + optional sticky composers
              // (e.g. lead-detail note bar sets --sticky-composer-clearance).
              bottom:
                'calc(6rem + max(0.75rem, calc(0.75rem + env(safe-area-inset-bottom))) + var(--sticky-composer-clearance, 0px))',
              zIndex: 10001,
            }
          : {
              // Bottom right keeps page-header actions clickable (DEV-556);
              // 5rem clears the floating get-started pill.
              bottom: '5rem',
              right: '1rem',
              zIndex: 10001,
            }
      }
      icons={{
        success: <CircleCheck className="h-4 w-4 text-primary" />,
        info: <Info className="h-4 w-4 text-primary" />,
        warning: <TriangleAlert className="h-4 w-4 text-amber-500" />,
        error: <OctagonX className="h-4 w-4 text-destructive" />,
        loading: <LoaderCircle className="h-4 w-4 animate-spin text-primary" />,
      }}
      {...props}
    />
  );
};

export { Toaster };
